import { AppError } from "models/app-error";
import { VgApiResult, vgApiRequest, vgApiUpload } from "./vg-api.service";
import patientFallbackImage from "assets/images/patient-fallback.svg";
import { speciesLabel } from "constants/patientEnums";

export type PatientRow = {
  id: string;
  image: string;
  name: string;
  type: string;
};

export type AnimalDetail = {
  animalId: number;
  orgId: number;
  name: string;
  animalSpecies?: number;
  animalBreed?: number;
  mainColour?: string;
  secondaryColour?: string;
  dateOfBirth?: string;
  microchipId?: string;
  insuranceReferenceNumber?: string;
  passportNumber?: string;
  gender?: number;
  desexedStatus?: number;
  bloodGroup?: number;
  isActive?: number;
  deceasedStatus?: number;
  deceasedDate?: string;
  ageString?: string;
  cloudFileUrl?: string;
};

type ReadOrgAnimalsResponse = {
  animalList?: unknown[];
  AnimalList?: unknown[];
};

function pluckAnimalList(data: ReadOrgAnimalsResponse | null | undefined): unknown[] {
  if (!data) return [];
  return (data.animalList ?? data.AnimalList ?? []) as unknown[];
}

function pickAnimal(data: Record<string, unknown> | null | undefined): Partial<AnimalDetail> {
  if (!data) return {};
  return {
    animalId: Number(data.animalId ?? data.AnimalId ?? 0),
    orgId: Number(data.orgId ?? data.OrgId ?? 0),
    name: String(data.name ?? data.Name ?? ""),
    animalSpecies:
      data.animalSpecies != null ? Number(data.animalSpecies) : undefined,
    animalBreed:
      data.animalBreed != null ? Number(data.animalBreed) : undefined,
    mainColour:
      data.mainColour != null ? String(data.mainColour) : undefined,
    secondaryColour:
      data.secondaryColour != null ? String(data.secondaryColour) : undefined,
    dateOfBirth:
      data.dateOfBirth != null ? String(data.dateOfBirth) : undefined,
    microchipId: data.microchipId != null ? String(data.microchipId) : undefined,
    insuranceReferenceNumber:
      data.insuranceReferenceNumber != null
        ? String(data.insuranceReferenceNumber)
        : undefined,
    passportNumber:
      data.passportNumber != null ? String(data.passportNumber) : undefined,
    gender: data.gender != null ? Number(data.gender) : undefined,
    desexedStatus:
      data.desexedStatus != null ? Number(data.desexedStatus) : undefined,
    bloodGroup: data.bloodGroup != null ? Number(data.bloodGroup) : undefined,
    isActive: data.isActive != null ? Number(data.isActive) : undefined,
    deceasedStatus:
      data.deceasedStatus != null ? Number(data.deceasedStatus) : undefined,
    deceasedDate:
      data.deceasedDate != null ? String(data.deceasedDate) : undefined,
    ageString:
      data.ageString != null ? String(data.ageString) : undefined,
    cloudFileUrl:
      data.cloudFileUrl != null ? String(data.cloudFileUrl) : undefined,
  };
}

type PatientsApiResponse = {
  data: PatientRow[] | null;
  error: AppError | null;
};

/**
 * How many patients a type-ahead asks for at a time. Enough to fill a dropdown
 * without the user scrolling, small enough that the query stays instant.
 */
export const PATIENT_SEARCH_PAGE_SIZE = 20;

export function mapRawAnimalsToPatientRows(raw: unknown[]): PatientRow[] {
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    const cloudFileUrl = String(row.cloudFileUrl ?? row.CloudFileUrl ?? "").trim();
    return {
      id: String(row.animalId ?? row.AnimalId ?? ""),
      image: cloudFileUrl || patientFallbackImage,
      name: String(row.name ?? row.Name ?? "Unknown"),
      type: speciesLabel(row.animalSpecies as number ?? row.AnimalSpecies as number) || "N/A",
    };
  });
}

export type AnimalCompleteResponse = {
  contactList?: unknown[];
  ContactList?: unknown[];
};

export async function readAnimalCompleteByAnimalId(
  accessToken: string,
  orgId: number,
  animalId: number
): Promise<VgApiResult<AnimalCompleteResponse>> {
  return vgApiRequest<AnimalCompleteResponse>(
    accessToken,
    "Read_AnimalComplete_By_Animal_ID",
    { orgId, animalId }
  );
}

export type MedicalRecordResponse = {
  medicalNotes?: unknown[];
  MedicalNotes?: unknown[];
  medicalRecordId?: number;
  MedicalRecordId?: number;
};

export type MedicalNoteDetail = {
  medicalNoteId: number;
  medicalRecordId: number;
  orgId: number;
  medicalNoteType: number;
  noteDescription: string;
  createdOn_UTC?: string;
};

export async function readMedicalRecordByOrgAndAnimalId(
  accessToken: string,
  orgId: number,
  animalId: number
): Promise<VgApiResult<MedicalRecordResponse>> {
  return vgApiRequest<MedicalRecordResponse>(
    accessToken,
    "Read_MedicalRecord_By_Org_And_Animal_ID",
    { orgId, animalId }
  );
}

export async function createMedicalNote(
  accessToken: string,
  payload: {
    orgId: number;
    medicalRecordId: number;
    medicalNoteType: number;
    noteDescription: string;
    createdOn_UTC?: string;
  }
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_MedicalNote", {
    orgId: payload.orgId,
    medicalRecordId: payload.medicalRecordId,
    medicalNoteType: payload.medicalNoteType,
    noteDescription: payload.noteDescription,
    createdOn_UTC: payload.createdOn_UTC ?? null,
  });
}

export async function updateMedicalNote(
  accessToken: string,
  payload: {
    orgId: number;
    medicalRecordId: number;
    medicalNoteId: number;
    medicalNoteType: number;
    noteDescription: string;
    createdOn_UTC?: string;
  }
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_MedicalNote", {
    orgId: payload.orgId,
    medicalRecordId: payload.medicalRecordId,
    medicalNoteId: payload.medicalNoteId,
    medicalNoteType: payload.medicalNoteType,
    noteDescription: payload.noteDescription,
    createdOn_UTC: payload.createdOn_UTC ?? null,
  });
}

export async function deleteMedicalNote(
  accessToken: string,
  orgId: number,
  medicalNoteId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_MedicalNote", {
    orgId,
    medicalNoteId,
  });
}

export const getPatientsList = async (
  accessToken: string,
  orgId: number
): Promise<PatientsApiResponse> => {
  const { data, error } = await vgApiRequest<ReadOrgAnimalsResponse>(
    accessToken,
    "Read_All_Org_Animals_By_Org_ID",
    { orgId }
  );
  if (error) {
    return { data: null, error };
  }
  const raw = pluckAnimalList(data ?? undefined);
  const patients = mapRawAnimalsToPatientRows(raw);

  return {
    data: patients,
    error: null,
  };
};

export async function createOrgAnimal(
  accessToken: string,
  payload: {
    orgId: number;
    name: string;
    animalSpecies: number;
    dateOfBirth?: string;
    gender?: number;
    bloodGroup?: number;
    microchipId?: string;
    insuranceReferenceNumber?: string;
    passportNumber?: string;
    isActive?: boolean | number;
    deceasedStatus?: number;
    deceasedDate?: string;
    animalBreed?: number;
    mainColour?: string;
    secondaryColour?: string;
    desexedStatus?: number;
  }
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  const activationState =
    typeof payload.isActive === "boolean"
      ? payload.isActive
        ? 2 // ACTIVE
        : 3 // INACTIVE
      : payload.isActive;

  // Backend Is_Valid_String rejects null/empty for these — coerce to a sentinel.
  const nonEmpty = (s: string | undefined, fallback: string) =>
    s && s.length > 0 ? s : fallback;

  return vgApiRequest(accessToken, "Create_Org_Animal", {
    orgId: payload.orgId,
    name: payload.name,
    animalSpecies: payload.animalSpecies,
    // Backend validator requires a parseable ISO instant >= Unix epoch (1970-01-01).
    dateOfBirth: payload.dateOfBirth ?? "1970-01-01T00:00:00.000Z",
    gender: payload.gender ?? 2, // Male (validator rejects Unknown=1)
    bloodGroup: payload.bloodGroup ?? 14, // NotApplicable
    microchipId: nonEmpty(payload.microchipId, "Unspecified"),
    insuranceReferenceNumber: nonEmpty(payload.insuranceReferenceNumber, "Unspecified"),
    passportNumber: nonEmpty(payload.passportNumber, "Unspecified"),
    isActive: activationState ?? 2, // ACTIVE
    deceasedStatus: payload.deceasedStatus ?? 2, // ALIVE
    // Backend validator requires a parseable ISO instant >= Unix epoch even when not deceased.
    deceasedDate: payload.deceasedDate ?? "1970-01-01T00:00:00.000Z",
    animalBreed: payload.animalBreed ?? 2, // Other (1=Unknown is rejected by validator)
    mainColour: nonEmpty(payload.mainColour, "#000000"),
    secondaryColour: nonEmpty(payload.secondaryColour, "#ffffff"),
    desexedStatus: payload.desexedStatus ?? 4, // NOT_APPLICABLE
  });
}

export async function readAnimal(
  accessToken: string,
  orgId: number,
  animalId: number
): Promise<VgApiResult<AnimalDetail>> {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(
    accessToken,
    "Read_Animal_By_Animal_ID",
    { orgId, animalId }
  );
  if (error) return { data: null, error };
  const p = pickAnimal(data);
  return {
    data: {
      animalId: p.animalId ?? animalId,
      orgId: p.orgId ?? orgId,
      name: p.name ?? "",
      animalSpecies: p.animalSpecies,
      animalBreed: p.animalBreed,
      mainColour: p.mainColour,
      secondaryColour: p.secondaryColour,
      dateOfBirth: p.dateOfBirth,
      microchipId: p.microchipId,
      insuranceReferenceNumber: p.insuranceReferenceNumber,
      passportNumber: p.passportNumber,
      gender: p.gender,
      desexedStatus: p.desexedStatus,
      bloodGroup: p.bloodGroup,
      isActive: p.isActive,
      deceasedStatus: p.deceasedStatus,
      deceasedDate: p.deceasedDate,
      ageString: p.ageString,
      cloudFileUrl: p.cloudFileUrl,
    },
    error: null,
  };
}

export async function updateOrgAnimal(
  accessToken: string,
  payload: {
    orgId: number;
    animalId: number;
    name: string;
    animalSpecies?: number;
    dateOfBirth?: string;
    gender?: number;
    bloodGroup?: number;
    microchipId?: string;
    insuranceReferenceNumber?: string;
    passportNumber?: string;
    isActive?: boolean | number;
    deceasedStatus?: number;
    deceasedDate?: string;
    animalBreed?: number;
    mainColour?: string;
    secondaryColour?: string;
    desexedStatus?: number;
  }
): Promise<VgApiResult<unknown>> {
  const activationState =
    typeof payload.isActive === "boolean"
      ? payload.isActive
        ? 2 // ACTIVE
        : 3 // INACTIVE
      : payload.isActive;

  const nonEmpty = (s: string | undefined, fallback: string) =>
    s && s.length > 0 ? s : fallback;

  return vgApiRequest(accessToken, "Update_Org_Animal", {
    orgId: payload.orgId,
    animalId: payload.animalId,
    name: payload.name,
    animalSpecies: payload.animalSpecies ?? 5, // CANINE
    // Backend validator requires a parseable ISO instant >= Unix epoch (1970-01-01).
    dateOfBirth: payload.dateOfBirth ?? "1970-01-01T00:00:00.000Z",
    gender: payload.gender ?? 2, // Male (validator rejects Unknown=1)
    bloodGroup: payload.bloodGroup ?? 14, // NotApplicable
    microchipId: nonEmpty(payload.microchipId, "Unspecified"),
    insuranceReferenceNumber: nonEmpty(payload.insuranceReferenceNumber, "Unspecified"),
    passportNumber: nonEmpty(payload.passportNumber, "Unspecified"),
    isActive: activationState ?? 2, // ACTIVE
    deceasedStatus: payload.deceasedStatus ?? 2, // ALIVE
    // Backend validator requires a parseable ISO instant >= Unix epoch even when not deceased.
    deceasedDate: payload.deceasedDate ?? "1970-01-01T00:00:00.000Z",
    animalBreed: payload.animalBreed ?? 2, // Other (1=Unknown is rejected by validator)
    mainColour: nonEmpty(payload.mainColour, "#000000"),
    secondaryColour: nonEmpty(payload.secondaryColour, "#ffffff"),
    desexedStatus: payload.desexedStatus ?? 4, // NOT_APPLICABLE
    ageString: "",
  });
}

export async function deleteOrgAnimal(
  accessToken: string,
  orgId: number,
  animalId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Animal_By_Animal_ID", { orgId, animalId });
}

export type AnimalImageUploadResult = {
  cloudFileUrl?: string;
};

function pickAnimalCloudFileUrl(
  data: Record<string, unknown> | null | undefined
): string | undefined {
  if (!data) return undefined;
  const url =
    data.cloudFileUrl ??
    data.CloudFileUrl ??
    data.fileUrl ??
    data.FileUrl ??
    data.url ??
    data.Url;
  return url != null ? String(url) : undefined;
}

/**
 * Multipart upload to UploadAnimalImage. Controller binds Params in the order
 * ["orgId", "animalId"] (see BaseDotNetCoreWebApiController.cs).
 */
export async function uploadAnimalImage(
  accessToken: string,
  orgId: number,
  animalId: number,
  file: File
): Promise<VgApiResult<AnimalImageUploadResult>> {
  const { data, error } = await vgApiUpload<Record<string, unknown>>(
    accessToken,
    "UploadAnimalImage",
    [file],
    [orgId, animalId]
  );
  if (error) return { data: null, error };
  return {
    data: { cloudFileUrl: pickAnimalCloudFileUrl(data) },
    error: null,
  };
}

/**
 * Link an existing animal to a contact via the AnimalContact mapping.
 * Backend: Create_Animal_Contact_Mapping (DC_Org_Map_AnimalContact { orgId, contactId, animalId }).
 */
export async function linkAnimalToContact(
  accessToken: string,
  orgId: number,
  contactId: number,
  animalId: number
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_Animal_Contact_Mapping", {
    orgId,
    contactId,
    animalId,
  });
}

export async function unlinkAnimalFromContact(
  accessToken: string,
  orgId: number,
  contactId: number,
  animalId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Animal_Contact_Mapping", {
    orgId,
    contactId,
    animalId,
  });
}

/**
 * The animal ids belonging to a client.
 *
 * Booking screens offered every animal in the clinic regardless of who was
 * paying, which on a busy list invites booking the wrong one. This narrows them.
 *
 * Ids rather than whole animals: the caller already has the org's patients
 * loaded, so a set of ids is enough to filter, and fetching each animal again
 * would be a round trip per patient.
 */
export async function readAnimalIdsForContact(
  accessToken: string,
  orgId: number,
  contactId: number
): Promise<VgApiResult<number[]>> {
  const { data, error } = await vgApiRequest<{
    listOfIDs?: unknown[];
    ListOfIDs?: unknown[];
  }>(accessToken, "Read_Animal_IDs_By_Contact_ID", { orgId, contactId });

  if (error) return { data: null, error };
  const list = data?.listOfIDs ?? data?.ListOfIDs ?? [];
  return {
    data: list.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
    error: null,
  };
}

/**
 * Patients matching what someone has typed, a page at a time.
 *
 * The alternative, and what the pickers did until now, is to load every animal
 * in the clinic and filter in the browser. That is fine at a few hundred and
 * falls over well before a real practice's list does.
 *
 * Pass contactId to search only one client's animals; leave it out to search the
 * whole clinic. The page is a range rather than a count, which is how the
 * platform models paging: pagingStart 0 and pagingStop 20 is the first twenty.
 */
export async function searchPatients(
  accessToken: string,
  input: {
    orgId: number;
    term: string;
    contactId?: number;
    pagingStart?: number;
    pagingStop?: number;
  }
): Promise<VgApiResult<AnimalDetail[]>> {
  const pagingStart = input.pagingStart ?? 0;
  const pagingStop = input.pagingStop ?? pagingStart + PATIENT_SEARCH_PAGE_SIZE;

  const { data, error } = await vgApiRequest<ReadOrgAnimalsResponse>(
    accessToken,
    "Read_Animals_By_Search",
    {
      orgId: input.orgId,
      // Zero means "not narrowed to a client". Sending -1, the usual unset id,
      // would search for a contact that cannot exist and come back empty.
      contactId: input.contactId ?? 0,
      textString: input.term.trim(),
      pagingStart,
      pagingStop,
    }
  );

  if (error) return { data: null, error };
  return {
    data: pluckAnimalList(data).map(
      (item) => pickAnimal(item as Record<string, unknown>) as AnimalDetail
    ),
    error: null,
  };
}
